import { useState } from 'react'
import { Outlet, createSearchParams, useSearchParams } from 'react-router-dom'

import { Card, Grid, Icon } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  NewRequestPageDocument,
  ProcurementType,
  ProcurementTypeItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import WithPaywall from 'modules/procurement/components/WithPaywall'
import { NewPurchaseRequestModal } from 'modules/procurement/components/purchaseRequests/NewRequestModal'

const NewRequestPage = () => {
  const { navigation } = useNavigation()
  const [searchParams, _setSearchParams] = useSearchParams()
  const [selectedType, setSelectedType] = useState<ProcurementTypeItemFragment | null>(null)

  const { company } = useCurrentSession()

  const { data, error, loading } = useQuery(NewRequestPageDocument, {
    variables: { companyId: company.id, enabled: true },
  })

  const requestTypes = data?.procurement.typesConnection.nodes || []

  const currencies = data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) || []

  const userCurrency =
    data?.apiCore?.currentsConnection.nodes[0]?.employee?.legalEntity?.currency || ''

  const hasCustomFormsEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_procurement_custom_forms'
    )?.enabled ?? false

  const getIcon = (type: ProcurementType['identifier']) => {
    switch (type) {
      case 'software':
        return 'floppy-disk'
      case 'other':
        return 'suitcase'
      default:
        return 'template'
    }
  }

  return (
    <WithPaywall
      allowWhileLoading
      features={[Features.procurement_purchase_orders]}
      modules={[AvailableModules.procurement]}
    >
      {() => (
        <ResponsivePage.Body
          variant="wide"
          authorized={!!data?.procurement._policies.purchaseRequestCreate.can}
          loading={loading}
          error={error}
        >
          <Grid variant="medium">
            {requestTypes &&
              requestTypes.map((requestType) => (
                <Card
                  key={requestType.identifier}
                  to={{
                    pathname: navigation.myPurchases.newRequest.fullPath(),
                    search: createSearchParams({ modal: 'new' }).toString(),
                  }}
                  onClick={() => setSelectedType(requestType)}
                >
                  <Card.Eyebrow>
                    <Icon.Large icon={getIcon(requestType.identifier)} color="radicalRed1000" />
                  </Card.Eyebrow>
                  <Card.Title>{requestType.name}</Card.Title>
                  <Card.Description>{requestType.description}</Card.Description>
                </Card>
              ))}
          </Grid>
          {searchParams.get('modal') === 'new' && (
            <NewPurchaseRequestModal
              procurementType={selectedType}
              currencies={currencies}
              defaultCurrency={userCurrency}
              hasCustomFormsEnabled={hasCustomFormsEnabled}
            />
          )}
          <Outlet />
        </ResponsivePage.Body>
      )}
    </WithPaywall>
  )
}

export default NewRequestPage
